import { useState } from 'react';
import { toast } from 'react-hot-toast';
import numeral from 'numeral';

import Select from 'react-select';
import Loader from '../Loader';

import useXeroContacts from '../../hooks/useXeroContacts';
import useXeroQuote from '../../hooks/useXeroQuote';

import designAndEngineeringProjectService from '../../services/designAndEngineeringProjectService';
import xeroService from '../../services/xeroService';

const engineeringMargin = (item) => {
  return item.engineering * item.marginPercentage / 100;
}

const engineeringTotal = (item) => {
  return item.engineering + engineeringMargin(item);
}

const designMargin = (item) => {
  return item.design * item.marginPercentage / 100;
};

const designTotal = (item) => {
  return item.design + designMargin(item);
};

const consultMargin = (item) => {
  if (item.consultMargin) {
    return item.consult * item.marginPercentage / 100;
  }

  return 0;
};

const consultTotal = (item) => {
  return item.consult + consultMargin(item);
}

const getBillableValue = (item) => {
  return engineeringTotal(item) + designTotal(item) + consultTotal(item);
}

const ProjectQuoteView = ({ project, onQuoteCreated }) => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: xeroContactData, isLoading } = useXeroContacts({ shouldFetch: !project.quoteId });

  const contacts = xeroContactData?.contacts?.contacts;

  const {
    data: xeroQuoteData,
    isLoading: isLoadingQuote,
    mutate: mutateQuote
  } = useXeroQuote({ shouldFetch: !!project.quoteId, quoteId: project.quoteId });

  const quote = xeroQuoteData?.quote;

  const handleCreateQuote = async () => {
    try {
      setIsSubmitting(true);

      const lineItems = project.items
        .filter(i => i.accepted)
        .map(item => ({
          Description: item.name,
          Quantity: 1,
          UnitAmount: getBillableValue(item),
          AccountCode: '200',
          TaxType: 'OUTPUT',
        }));

      const quoteNumberSuffix = 'QU-0' + String(project.aseId.split('-')[1]);

      const createdQuote = await xeroService.createQuote({
        quoteNumber: quoteNumberSuffix,
        contactId: selectedContact.value,
        lineItems
      });

      await designAndEngineeringProjectService.updateDesignAndEngineeringProject({
        designAndEngineeringProjectId: project._id,
        updateData: {
          quoteId: createdQuote.QuoteID
        }
      });

      project.quoteId = createdQuote.QuoteID;

      await mutateQuote();

      onQuoteCreated();

      toast.success('Quote created successfully');
    } catch (error) {
      toast.error('Failed to create quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingQuote || isSubmitting) {
    return (
      <div className='flex items-center justify-center w-full h-full'>
        <Loader />
      </div>
    )
  }

  if (!project?.quoteId) {
    return (
      <div className='flex flex-col items-center justify-center p-4 w-full'>
        <p className='mb-4 text-lg font-semibold'>Generate quote</p>

        <Select
          value={selectedContact}
          onChange={setSelectedContact}
          className='min-w-[300px] mb-4'
          options={contacts?.map(contact => ({ value: contact.ContactID, label: contact.Name }))}
          placeholder='Select a contact'
          isLoading={isLoading}
        />

        <button
          onClick={handleCreateQuote}
          className='bg-ase-blue text-white font-semibold h-[36px] rounded-md flex items-center justify-center gap-1 text-sm px-4 py-2'
        >
          Create quote
        </button>
      </div>
    )
  }

  const viewQuote = () => {
    const organisationId = '!19NC8';

    const xeroUrl = `https://go.xero.com/app/${organisationId}/quotes/view/${project.quoteId}`;

    window.open(xeroUrl, '_blank');
  }

  const updateQuote = async () => {
    try {
      setIsSubmitting(true);

      const lineItems = project.items
        .filter(i => i.accepted)
        .map(item => ({
          Description: item.name,
          Quantity: 1,
          UnitAmount: getBillableValue(item),
          AccountCode: '200'
        }));

      await xeroService.updateQuote({
        quoteId: project.quoteId,
        lineItems,
        date: quote.Date,
        contactId: quote?.Contact?.ContactID
      });

      await mutateQuote();

      toast.success('Quote updated successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update quote')
    } finally {
      setIsSubmitting(false);
    }
  }

  const deleteQuote = async () => {
    try {
      setIsSubmitting(true);

      await xeroService.updateQuote({
        quoteId: project.quoteId,
        status: 'DELETED',
        date: quote.Date,
        contactId: quote?.Contact?.ContactID,
        lineItems: quote.LineItems
      });

      await designAndEngineeringProjectService.updateDesignAndEngineeringProject({
        designAndEngineeringProjectId: project._id,
        updateData: {
          quoteId: null
        }
      });

      project.quoteId = null;

      await mutateQuote();

      toast.success('Quote deleted successfully');
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete quote');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div>
      <p>Quote ID: {quote.QuoteID}</p>
      <p>Quote status: {quote.Status}</p>
      <p className='mb-4'>
        Quote total: {numeral(quote.Total).format('$0,0.00')}
      </p>

      <div className='flex items-center w-full gap-2'>
        <button
          onClick={viewQuote}
          className='bg-ase-blue rounded-md px-2 py-1 text-sm text-white'
        >
          View
        </button>

        <button
          onClick={updateQuote}
          className='bg-yellow-600 rounded-md px-2 py-1 text-sm text-white'
        >
          Update
        </button>

        <button
          onClick={deleteQuote}
          className='bg-red-600 rounded-md px-2 py-1 text-sm text-white'
        >
          Delete
        </button>
      </div>
    </div>
  )
}

export default ProjectQuoteView;
